import React, { Component, ErrorInfo } from 'react';
import { useLocation } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback UI. If provided, overrides the default branded fallback. */
  fallback?: React.ReactNode;
  /** Callback fired whenever an error is caught. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** A location key. When it changes the boundary auto-resets. Passed by the HOC wrapper. */
  locationKey?: string;
}

// ─── Default Fallback UI ──────────────────────────────────────────────────────

function DefaultFallback({ onReset }: { onReset: () => void }) {
  return (
    <div className="portal-typography min-h-screen bg-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {/* Honeycomb icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center border-2 border-[#242424]/10">
            <svg
              className="w-8 h-8 text-[#242424]"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              {/* Simple hex / honeycomb shape */}
              <path
                d="M12 2L20.66 7V17L12 22L3.34 17V7L12 2Z"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinejoin="round"
              />
              <path
                d="M12 8L16.33 10.5V15.5L12 18L7.67 15.5V10.5L12 8Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold text-[#242424] mb-2">
          Something went wrong
        </h1>

        {/* Body */}
        <p className="text-sm text-gray-500 mb-8 leading-relaxed">
          An unexpected error occurred. Your data is safe — please reload the
          page or return to your dashboard.
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onReset}
            className="px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm
                       border border-[#242424]/10 hover:bg-[#f0e520] active:scale-95
                       transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary/60"
          >
            Reload page
          </button>
          <a
            href="/portal"
            className="px-6 py-2.5 rounded-lg bg-transparent text-[#242424] font-medium text-sm
                       border border-[#242424]/20 hover:bg-gray-50 active:scale-95
                       transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            Go to dashboard
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Class Component ──────────────────────────────────────────────────────────

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Auto-reset when the route location changes (resetOnNavigate behaviour)
    if (
      this.state.hasError &&
      prevProps.locationKey !== this.props.locationKey
    ) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return <>{this.props.fallback}</>;
    }

    return <DefaultFallback onReset={this.handleReset} />;
  }
}

// ─── HOC Wrapper (with resetOnNavigate) ──────────────────────────────────────

/**
 * Wraps a component in an ErrorBoundary that resets automatically on navigation.
 * Uses useLocation internally to track route changes.
 */
function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  boundaryProps?: Omit<ErrorBoundaryProps, 'children' | 'locationKey'>
) {
  function WithErrorBoundaryWrapper(props: P) {
    const location = useLocation();
    return (
      <ErrorBoundary {...boundaryProps} locationKey={location.key}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  }

  WithErrorBoundaryWrapper.displayName = `WithErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithErrorBoundaryWrapper;
}

export { withErrorBoundary };
export default ErrorBoundary;
